import customAxios from '../../util/Api';
import authHeader from '../../../../services/auth/jwt/auth-header';
import * as XLSX from 'xlsx';

const EXPECTED_HEADERS = ['Customer', 'CustomerDocketNumber', 'CustomerMC', 'NetWeightInTonnes', 'ValueInEuros'];

// Returns { value, invalid } — value is null for both "intentionally blank"
// and "garbage input" cases, but invalid=true only for the latter.
// Valid values are rounded to 2 decimal places (e.g. 54 -> 54.00) to match
// the decimal(x,2) columns on the backend.
const parseNumericCell = raw => {
  if (raw === null || raw === undefined) return { value: null, invalid: false };
  const str = String(raw).trim();
  if (str === '' || str.toUpperCase() === 'NULL') return { value: null, invalid: false };
  const num = Number(str);
  if (Number.isNaN(num)) return { value: null, invalid: true }; // had content, didn't parse
  return { value: Number(num.toFixed(2)), invalid: false };
};

export class CustomerMcValidationError extends Error {
  constructor(message, { missing = [], unexpected = [] } = {}) {
    super(message);
    this.name = 'CustomerMcValidationError';
    this.missing = missing;
    this.unexpected = unexpected;
  }
}

const validateHeaders = actualHeaders => {
  const missing = EXPECTED_HEADERS.filter(h => !actualHeaders.includes(h));
  if (missing.length > 0) {
    throw new CustomerMcValidationError(`Excel file is missing required column(s): ${missing.join(', ')}`, { missing });
  }
};

// The file can contain rows for multiple different customers — the only
// requirement per-row is that the Customer column isn't blank.
export const parseCustomerMcExcel = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const headerRow = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0] || [];
        validateHeaders(headerRow.map(h => String(h).trim()));

        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        if (rows.length === 0) {
          throw new CustomerMcValidationError('Excel file has no data rows.');
        }

        // Matches AddCustomerWoodchipSamples model — ASP.NET Core's JSON
        // binder is case-insensitive so camelCase here is fine.
        const payload = rows.map((row, idx) => {
          const customer = String(row['Customer'] ?? '').trim();
          const mc = parseNumericCell(row['CustomerMC']);
          const weight = parseNumericCell(row['NetWeightInTonnes']);
          const value = parseNumericCell(row['ValueInEuros']);

          // Hard errors: Customer and CustomerMC are mandatory. These block
          // the upload until the source file is fixed — no way to acknowledge past them.
          const hardErrors = [];
          if (!customer) {
            hardErrors.push({ field: 'customer', message: 'Customer is required.' });
          }
          if (mc.value === null) {
            hardErrors.push({
              field: 'customerMC',
              message: mc.invalid ? 'CustomerMC % is invalid.' : 'CustomerMC % is required.',
            });
          }

          // Soft errors: NetWeightInTonnes / ValueInEuros are optional floats.
          // Garbage input here just becomes blank once acknowledged (unchanged behaviour).
          const softErrors = [];
          if (weight.invalid) {
            softErrors.push({ field: 'netWeightInTonnes', message: 'Net weight is invalid and will upload as blank.' });
          }
          if (value.invalid) {
            softErrors.push({ field: 'valueInEuros', message: 'Value is invalid and will upload as blank.' });
          }

          return {
            _rowNum: idx + 2, // +2 = account for header row + 0-index, matches excel row number
            customer,
            customerDocketNumber: String(row['CustomerDocketNumber'] ?? '').trim(),
            customerMC: mc.value,
            netWeightInTonnes: weight.value,
            valueInEuros: value.value,
            hardErrors,
            softErrors,
            // kept for backwards compatibility with any existing highlighting logic
            invalidFields: [...hardErrors, ...softErrors].map(err => err.field),
          };
        });

        resolve(payload);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsBinaryString(file);
  });

export const uploadCustomerMcSamples = async rows => {
  const payload = rows.map(({ _rowNum, invalidFields, hardErrors, softErrors, ...cleanRow }) => cleanRow);

  try {
    const response = await customAxios.post('/Ops/AddCustomerWoodchipSample', payload, {
      headers: authHeader(),
    });

    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || err.message || 'Upload failed');
  }
};
